import gql from "graphql-tag";

import { SystemBoundary } from "../__generated__/globalTypes";
import {
  useOrganization,
  useOrganizationOrNull,
} from "../components/organizations/OrganizationProvider";
import { ImpactCategory } from "../domain/impactCategories";

export function useAccurateCookingImpacts(): boolean {
  const [organization] = useOrganization();
  return organization.features.accurateCookingImpacts;
}

export function useAccuratePostPreparationStorageImpacts(): boolean {
  const [organization] = useOrganization();
  return organization.features.accuratePostPreparationStorageImpacts;
}

export default function useAvailableImpactCategories(): Array<ImpactCategory> {
  return [
    ImpactCategory.GHG,
    ImpactCategory.LAND_USE,
    ImpactCategory.WATER_USE,
  ];
}

export function useBrandedIngredients(): boolean {
  const [organization] = useOrganization();
  return organization.features.brandedIngredients;
}

export function useLabels(): boolean {
  const [organization] = useOrganization();
  return organization.features.labels;
}

export function useProductTags(): boolean {
  const [organization] = useOrganization();
  return organization.features.productTags;
}

export function useCountryOfOriginSourcing(): boolean {
  const [organization] = useOrganization();
  return organization.features.countryOfOriginSourcing;
}

export function useDashboard(): boolean {
  const [organization] = useOrganization();
  return organization.features.dashboard;
}

export function useDataQualityScore(): boolean {
  const [organization] = useOrganization();
  return organization.features.dataQualityScore;
}

export function useDetailedLifecycleAnalysis(): boolean {
  const [organization] = useOrganization();
  return organization.features.detailedLifeCycleAnalysis;
}

export function useEditProducts(): boolean {
  const [organization] = useOrganization();
  return organization.features.editProducts;
}

export function useExportIngredientImpactsByStage(): boolean {
  const [organization] = useOrganization();
  return organization.features.exportIngredientImpactsByStage;
}

export function useFoodManufacturerOrganization(): boolean {
  const [organization] = useOrganizationOrNull();
  return (
    organization !== null && organization.features.foodManufacturerOrganization
  );
}

// export const GREENCORE_ID = "5884fa39-3826-434a-91db-963d127f5fba"; // Viktor's org for testing
export const GREENCORE_ID = "66b2e76e-90f6-409b-b20b-df832351488c";

export function useFlag(): boolean {
  // TODO: add feature flag for FLAG page
  const [organization] = useOrganization();
  return organization.id === GREENCORE_ID;
}

export function useProductPackaging(): boolean {
  const [organization] = useOrganization();
  return organization.features.productPackaging;
}

export function useRequestImpactAnalysis(): boolean {
  const [organization] = useOrganization();
  return organization.features.requestImpactAnalysis;
}

export function useScope3Report(): boolean {
  const [organization] = useOrganization();
  return organization.features.scope3Report;
}

export function useSubproducts(): boolean {
  const [organization] = useOrganization();
  return organization.features.subproducts;
}

export function useSystemBoundary(): SystemBoundary {
  const [organization] = useOrganization();
  return organization.features.systemBoundary;
}

export function useOrganizationSystemBoundaryIsCradleToGrave(): boolean {
  const [organization] = useOrganization();
  return (
    organization.features.systemBoundary === SystemBoundary.CRADLE_TO_GRAVE
  );
}

export function useViewSharedProducts(): boolean {
  const [organization] = useOrganization();
  return organization?.features.viewSharedProducts;
}

export function useIsHeadOffice(): boolean {
  const [organization] = useOrganization();
  return organization.features.headOffice;
}

export function useSalesReport(): boolean {
  const [organization] = useOrganization();
  return organization.features.salesReport;
}

export function organizationFeaturesFragment() {
  return gql`
    fragment useOrganizationFeatures_Organization on Organization {
      features {
        accurateCookingImpacts
        accuratePostPreparationStorageImpacts
        brandedIngredients
        labels
        productTags
        countryOfOriginSourcing
        dashboard
        dataQualityScore
        detailedLifeCycleAnalysis
        editProducts
        exportIngredientImpactsByStage
        foodManufacturerOrganization
        headOffice
        productPackaging
        requestImpactAnalysis
        salesReport
        scope3Report
        subproducts
        systemBoundary
        viewSharedProducts
      }
    }
  `;
}
